import { useRef, useState } from 'react';
import moment from 'moment-timezone';
import { getReservationSlots } from '../../../../../../api/action/booking';

const useAvailability = (product, searchCriteria, selectedTime, companyState, companyPrimaryId, dispatch) => {
  const [availabilityData, setAvailabilityData] = useState(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [autoNavigateToReview, setAutoNavigateToReview] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);
  const normaliseTime = (raw) => {
    if (!raw) return null;
    if (raw.length > 8 && raw.includes('T')) return raw.substring(11, 16);
    return raw.substring(0, 5);
  };

  const overlapsSelectedTime = (slot, occurrenceDate, timeToCheck) => {
    if (!slot || !timeToCheck?.start || !timeToCheck?.end) return false;

    const selectedStart = moment(`${occurrenceDate}T${normaliseTime(timeToCheck.start)}`);
    const selectedEnd = moment(`${occurrenceDate}T${normaliseTime(timeToCheck.end)}`);
    const slotStart = moment(`${occurrenceDate}T${normaliseTime(slot.startTime)}`);
    const slotEnd = moment(`${occurrenceDate}T${normaliseTime(slot.endTime)}`);

    return slotStart.isBefore(selectedEnd) && slotEnd.isAfter(selectedStart);
  };

  const checkSlotConflictsForOccurrence = (groupSlots, occurrenceDate, timeToCheck) => {
    if (!Array.isArray(groupSlots) || !groupSlots.length || !timeToCheck?.start || !timeToCheck?.end) {
      return { available: true, conflictingSlots: [], availableSlots: [], suggestedProduct: null };
    }

    const overlappingSlots = groupSlots.filter((slot) =>
      overlapsSelectedTime(slot, occurrenceDate, timeToCheck),
    );

    const candidateSlots = overlappingSlots.length > 0 ? overlappingSlots : groupSlots;
    const isOpenSlot = (slot) =>
      slot.status === 'OPEN' || slot.status === 'AVAILABLE';
    const openSlots = candidateSlots.filter(isOpenSlot);
    const assignedProductIds = [
      ...new Set(
        openSlots
          .map((slot) => slot.assignedProductId)
          .filter(Boolean),
      ),
    ];
    const usesMultipleWorkspaces = assignedProductIds.length > 1;
    const preferredAssignedProductId = assignedProductIds[0] || null;
    const conflictingSlots = candidateSlots
      .filter((slot) => ['RESERVED', 'BOOKED', 'BLOCKED', 'NOT_AVAILABLE'].includes(slot.status))
      .map((slot) => ({
        start: slot.startTime,
        end: slot.endTime,
        status: slot.status,
        date: occurrenceDate,
        assignedProductId: slot.assignedProductId,
      }))
      .concat(
        usesMultipleWorkspaces
          ? candidateSlots
              .filter((slot) => slot.assignedProductId && slot.assignedProductId !== preferredAssignedProductId)
              .map((slot) => ({
                start: slot.startTime,
                end: slot.endTime,
                status: 'MIXED_WORKSPACE',
                date: occurrenceDate,
                assignedProductId: slot.assignedProductId,
              }))
          : [],
      );

    const chosenSlot = openSlots.find((slot) => slot.assignedProductId === preferredAssignedProductId)
      || openSlots[0]
      || candidateSlots[0]
      || null;
    const suggestedProductSlots = chosenSlot?.suggestedProduct?.slots || [];
    const availableSlots = suggestedProductSlots.filter(
      s => s.status === 'OPEN' || s.status === 'AVAILABLE'
    );
    const allRequestedSlotsAvailable = candidateSlots.length > 0
      && candidateSlots.every(isOpenSlot)
      && !usesMultipleWorkspaces;

    return {
      available: allRequestedSlotsAvailable,
      conflictingSlots,
      availableSlots,
      suggestedProduct: chosenSlot?.suggestedProduct || null,
      assignedProductId: chosenSlot?.assignedProductId || null,
    };
  };

  const checkRecurrenceAvailability = async (recurrenceData, occurrenceDates, shouldAutoNavigate = false) => {
    if (!recurrenceData || !product) return;

    //  RESET cancel flag before starting
    cancelRef.current = false;

    setIsCheckingAvailability(true);
    setError(null);

    if (shouldAutoNavigate) {
      setAutoNavigateToReview(true);
    }

    try {
      const recurrencePayload = {
        company: {
          primaryId: companyPrimaryId,
          domain: { type: "FLEX_OFFICE" },
          workingSchedule: companyState.workingSchedule
        },
        criteria: {
          durationType: "HOUR",
          product: { category: { primaryId: product.category?.primaryId } },
          searchPeriod: {
            effectiveDate: {
              localStartDate: `${searchCriteria.date}T00:00:00`,
              timeZone: companyState?.operatingLocation?.zoneId || 'America/New_York'
            }
          },
          recurrence: {
            frequency: recurrenceData.recurrenceType?.toUpperCase(),
            pattern: {
              recurEveryValue: recurrenceData.interval,
              everyWeekdayEnabled: recurrenceData.pattern === 'weekday'
            },
            range: {
              starts: `${searchCriteria.date}T00:00:00`,
              endType: recurrenceData.ends === 'after' ? 'ENDS_AFTER_OCCURRENCES' : 'ENDS_ON_DATE',
              endsAfterOccurrences: recurrenceData.ends === 'after' ? recurrenceData.occurrences : null,
              endsOnDate: recurrenceData.ends === 'on' ? `${recurrenceData.endDate}T00:00:00` : null
            }
          }
        }
      };

      const results = await dispatch(getReservationSlots(recurrencePayload));

      //  STOP if cancelled AFTER API
      if (cancelRef.current) return null;

      const availabilityResults = occurrenceDates.map((date) => ({
        date,
        available: true,
        conflictingSlots: [],
        availableSlots: [],
        productData: null
      }));

      const groups = results?.matchingProductGroups;
      if (!Array.isArray(groups) || groups.length === 0) {
        if (cancelRef.current) return null;
        setAvailabilityData(availabilityResults);
        return availabilityResults;
      }

      const matchingGroup = groups.find(group =>
        Array.isArray(group.productIds) && group.productIds.includes(product.primaryId)
      );

      if (!matchingGroup) {
        if (cancelRef.current) return null;
        setAvailabilityData(availabilityResults);
        return availabilityResults;
      }

      const slotsByDate = {};
      if (Array.isArray(matchingGroup.slots)) {
        matchingGroup.slots.forEach((slot) => {
          if (!slot?.date) return;
          if (!slotsByDate[slot.date]) {
            slotsByDate[slot.date] = [];
          }
          slotsByDate[slot.date].push(slot);
        });
      }

      occurrenceDates.forEach((date, index) => {
        if (cancelRef.current) return; //  STOP mid-loop

        const groupSlots = slotsByDate[date];
        if (!groupSlots?.length) return;

        const { available, conflictingSlots, availableSlots, suggestedProduct, assignedProductId } =
          checkSlotConflictsForOccurrence(groupSlots, date, selectedTime);

        availabilityResults[index] = {
          date,
          available,
          conflictingSlots,
          availableSlots,
          productData: suggestedProduct
            ? { ...suggestedProduct, slots: suggestedProduct.slots || [] }
            : null,
          assignedProductId
        };
      });

      if (cancelRef.current) return null;

      setAvailabilityData(availabilityResults);
      return availabilityResults;

    } catch (err) {
      if (!cancelRef.current) {
        setError('Failed to check availability for recurrence dates');
        setAutoNavigateToReview(false);
      }
      return null;
    } finally {
      //  DO NOT reset loading if cancelled (UI already closed)
      if (!cancelRef.current) {
        setIsCheckingAvailability(false);
      }
    }
  };

  const cancelCheck = () => {
    cancelRef.current = true;
    setIsCheckingAvailability(false);
  };

  const getAvailabilityStatus = (availabilityData, recurrenceData) => {
    if (!availabilityData || !recurrenceData) return null;

    const totalDates = availabilityData.length;
    const availableDates = availabilityData.filter(item => item.available).length;
    const unavailableDates = totalDates - availableDates;

    if (unavailableDates === 0) {
      return { type: 'success', message: `All ${totalDates} dates are available` };
    }
    return {
      type: 'warning',
      message: `${availableDates} of ${totalDates} dates available. ${unavailableDates} date(s) have conflicts.`
    };
  };

  return {
    availabilityData,
    setAvailabilityData,
    isCheckingAvailability,
    autoNavigateToReview,
    setAutoNavigateToReview,
    error,
    setError,
    checkRecurrenceAvailability,
    getAvailabilityStatus, cancelCheck
  };
};

export default useAvailability;
